import { useState, useRef, useEffect, useCallback } from "react";
import { X, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";
import { getFaceDetector, detectFaceBox } from "@/lib/faceDetect";

// ISO/IEC 7810 ID-1 (credit card size) specs for the three document types
const DOC_SPECS = {
  residence_card: { label: "在留カード", w: 85.6, h: 54, color: "#00A651" },
  drivers_license: { label: "運転免許証", w: 85.6, h: 54, color: "#2563eb" },
  my_number_card: { label: "マイナンバーカード", w: 85.6, h: 54, color: "#7c3aed" },
};

const PPI = 150;
const mmToPixels = (mm) => Math.round((mm / 25.4) * PPI);

export function compressImage(file, docType = "residence_card") {
  return new Promise((resolve) => {
    const done = (f) => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } resolve(f || file); };
    let url;
    try {
      const spec = DOC_SPECS[docType] || DOC_SPECS.residence_card;
      const targetW = mmToPixels(spec.w);
      const targetH = mmToPixels(spec.h);
      const fallback = setTimeout(() => done(file), 5000);
      const img = new Image();
      img.onerror = () => { clearTimeout(fallback); done(file); };
      img.onload = () => {
        clearTimeout(fallback);
        try {
          const canvas = document.createElement("canvas");
          let w = img.width;
          let h = img.height;
          const maxW = targetW * 2;
          const maxH = targetH * 2;
          if (w > maxW || h > maxH) {
            const ratio = Math.min(maxW / w, maxH / h);
            w = Math.round(w * ratio);
            h = Math.round(h * ratio);
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => done(blob ? new File([blob], file.name || "document.jpg", { type: "image/jpeg" }) : file),
            "image/jpeg",
            0.82
          );
        } catch (e) { done(file); }
      };
      url = URL.createObjectURL(file);
      img.src = url;
    } catch (e) { done(file); }
  });
}

export function DocumentCamera({ docType, side = "front", face = false, onCapture, onClose }) {
  const { t } = useI18n();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [facingMode, setFacingMode] = useState(face ? "user" : "environment");
  const spec = face ? { label: "顔写真", w: 3, h: 4, color: "#f97316" } : (DOC_SPECS[docType] || DOC_SPECS.residence_card);
  // Cards are sized by container width; the portrait face oval is sized by container HEIGHT
  // so it never overflows on wide desktop viewports.
  const FRAME_FRAC = face ? 0.62 : 0.85;

  const [autoMode, setAutoMode] = useState(true);
  const [autoProgress, setAutoProgress] = useState(0);
  const capturedRef = useRef(false);
  const prevSampleRef = useRef(null);
  const stableCountRef = useRef(0);

  const computeCropRect = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const vW = video.videoWidth;
    const vH = video.videoHeight;
    const rect = video.getBoundingClientRect();
    const cW = rect.width;
    const cH = rect.height;
    if (!cW || !cH) return null;
    const scale = Math.max(cW / vW, cH / vH);
    const offX = (vW * scale - cW) / 2;
    const offY = (vH * scale - cH) / 2;
    let frameW, frameH;
    if (face) {
      frameH = FRAME_FRAC * cH;
      frameW = frameH * (spec.w / spec.h);
    } else {
      frameW = FRAME_FRAC * cW;
      frameH = frameW * (spec.h / spec.w);
    }
    const frameLeft = (cW - frameW) / 2;
    const frameTop = (cH - frameH) / 2;
    let sx = (frameLeft + offX) / scale;
    let sy = (frameTop + offY) / scale;
    let sw = frameW / scale;
    let sh = frameH / scale;
    sx = Math.max(0, Math.min(sx, vW));
    sy = Math.max(0, Math.min(sy, vH));
    sw = Math.max(1, Math.min(sw, vW - sx));
    sh = Math.max(1, Math.min(sh, vH - sy));
    return { sx, sy, sw, sh };
  }, [spec.w, spec.h, FRAME_FRAC, face]);

  const startCamera = useCallback(async (facing) => {
    if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => setReady(true);
      }
    } catch (err) {
      // PC等で facingMode 要求が通らない場合は汎用カメラにフォールバック
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => setReady(true);
        }
      } catch (err2) {
        console.error("Camera error:", err2);
        onClose();
      }
    }
  }, [onClose]);

  useEffect(() => {
    startCamera(facingMode);
    return () => {
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [facingMode, startCamera]);

  const lastFaceBoxRef = useRef(null);
  const faceAlignedRef = useRef(false);
  const detectorRef = useRef(null);
  const detectorDoneRef = useRef(false);

  useEffect(() => {
    if (!face) return;
    let alive = true;
    getFaceDetector().then((d) => { if (alive) { detectorRef.current = d; detectorDoneRef.current = true; } });
    return () => { alive = false; };
  }, [face]);

  // 検出した顔の矩形から、枠比率(3:4)で顔を中央に収める“自動ズーム”クロップ矩形を計算
  const rectFromBox = useCallback((b, vW, vH) => {
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;
    // 顔検出枠は髪や頭頂を含まないため、上下に十分な余白を取り頭が切れないようにする
    let ch = Math.min(vH, b.height * 2.1);
    let cw = ch * (spec.w / spec.h);          // 3:4
    if (cw > vW) { cw = vW; ch = cw * (spec.h / spec.w); }
    let sx = cx - cw / 2;
    let sy = cy - ch * 0.55;                   // 顔をやや下寄せにして頭頂・髪の余白を確保
    sx = Math.max(0, Math.min(sx, vW - cw));
    sy = Math.max(0, Math.min(sy, vH - ch));
    return { sx, sy, sw: cw, sh: ch };
  }, [spec.w, spec.h]);

  const faceCropRect = useCallback(() => {
    const video = videoRef.current;
    const box = detectFaceBox(detectorRef.current, video);
    if (!box || !video) return null;
    return rectFromBox(box, video.videoWidth, video.videoHeight);
  }, [rectFromBox]);

  const doCapture = useCallback(async () => {
    if (capturedRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    capturedRef.current = true;
    let r = null;
    if (face) {
      const lb = lastFaceBoxRef.current;
      if (lb && Date.now() - lb.ts < 1500) r = rectFromBox(lb.box, video.videoWidth, video.videoHeight);
      if (!r) r = faceCropRect();
    }
    r = r || computeCropRect();
    if (!r) { capturedRef.current = false; return; }
    canvas.width = Math.round(r.sw);
    canvas.height = Math.round(r.sh);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async (blob) => {
      const file = new File([blob], "document-capture.jpg", { type: "image/jpeg" });
      const compressed = face ? file : await compressImage(file, docType);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      onCapture(compressed);
    }, "image/jpeg", 0.95);
  }, [computeCropRect, faceCropRect, rectFromBox, docType, onCapture, face]);

  const handleCapture = () => doCapture();

  useEffect(() => {
    if (!ready || !autoMode) { setAutoProgress(0); return; }
    capturedRef.current = false;
    stableCountRef.current = 0;
    prevSampleRef.current = null;
    setAutoProgress(0);
    faceAlignedRef.current = false;
    const checkFaceAlign = () => {
      if (!face) { faceAlignedRef.current = true; return; }
      if (detectorDoneRef.current && !detectorRef.current) { faceAlignedRef.current = true; return; } // 顔検出の読込失敗時は安定検知のみ
      const video = videoRef.current;
      const r = computeCropRect();
      const b = detectFaceBox(detectorRef.current, video);
      if (!b || !r) { faceAlignedRef.current = false; return; }
      lastFaceBoxRef.current = { box: b, ts: Date.now() };
      // 顔が枠(オーバル)内に完全に収まり、かつ十分な大きさの時だけアライン成立
      const withinX = b.x >= r.sx + r.sw * 0.04 && (b.x + b.width) <= r.sx + r.sw * 0.96;
      const withinY = b.y >= r.sy + r.sh * 0.04 && (b.y + b.height) <= r.sy + r.sh * 0.96;
      const sizeOk = b.height >= r.sh * 0.45 && b.height <= r.sh * 0.96;
      faceAlignedRef.current = withinX && withinY && sizeOk;
    };
    const SAMPLE_W = 48;
    const SAMPLE_H = Math.max(24, Math.round(SAMPLE_W * (spec.h / spec.w)));
    const CONTENT_MIN = 16;
    const MOTION_MAX = 9;
    const STABLE_NEEDED = 7;
    const warmupUntil = Date.now() + 900;
    const ac = document.createElement("canvas");
    ac.width = SAMPLE_W; ac.height = SAMPLE_H;
    const actx = ac.getContext("2d", { willReadFrequently: true });
    const id = setInterval(() => {
      if (capturedRef.current) return;
      if (face) checkFaceAlign();
      const video = videoRef.current;
      const r = computeCropRect();
      if (!video || !r) return;
      try {
        actx.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, SAMPLE_W, SAMPLE_H);
        const data = actx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data;
        const n = SAMPLE_W * SAMPLE_H;
        const lum = new Float32Array(n);
        let sum = 0;
        for (let i = 0; i < n; i++) {
          const p = i * 4;
          const l = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
          lum[i] = l; sum += l;
        }
        const mean = sum / n;
        let varsum = 0;
        for (let i = 0; i < n; i++) { const d = lum[i] - mean; varsum += d * d; }
        const std = Math.sqrt(varsum / n);
        let motion = 999;
        const prev = prevSampleRef.current;
        if (prev) {
          let ms = 0;
          for (let i = 0; i < n; i++) ms += Math.abs(lum[i] - prev[i]);
          motion = ms / n;
        }
        prevSampleRef.current = lum;
        if (Date.now() < warmupUntil) return;
        const good = std >= CONTENT_MIN && motion <= MOTION_MAX && (!face || faceAlignedRef.current);
        stableCountRef.current = good ? stableCountRef.current + 1 : 0;
        setAutoProgress(Math.min(1, stableCountRef.current / STABLE_NEEDED));
        if (stableCountRef.current >= STABLE_NEEDED) {
          clearInterval(id);
          doCapture();
        }
      } catch (e) { /* ignore */ }
    }, 220);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, autoMode, facingMode, side, computeCropRect, doCapture]);

  const toggleCamera = () => setFacingMode((f) => (f === "environment" ? "user" : "environment"));

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col" data-testid="document-camera">
      <div className="flex-1 relative overflow-hidden">
        <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />
        {ready && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 2 }}>
            <div className={`relative overflow-visible ${face ? "rounded-[50%]" : "rounded-xl"}`} style={{ ...(face ? { height: `${FRAME_FRAC * 100}%` } : { width: `${FRAME_FRAC * 100}%` }), aspectRatio: `${spec.w}/${spec.h}`, border: `2.5px solid ${spec.color}`, boxShadow: "0 0 0 9999px rgba(0,0,0,0.65)" }}>
              {!face && [
                { t: 0, l: 0, br: "border-t-[3px] border-l-[3px] rounded-tl-xl" },
                { t: 0, r: 0, br: "border-t-[3px] border-r-[3px] rounded-tr-xl" },
                { b: 0, l: 0, br: "border-b-[3px] border-l-[3px] rounded-bl-xl" },
                { b: 0, r: 0, br: "border-b-[3px] border-r-[3px] rounded-br-xl" },
              ].map((c, i) => (
                <div key={i} className={`absolute w-8 h-8 ${c.br}`} style={{ borderColor: spec.color, top: c.t, bottom: c.b, left: c.l, right: c.r }} />
              ))}
            </div>
            <div className="absolute top-4 left-0 right-0 flex items-center justify-center gap-2 px-4" style={{ zIndex: 3 }}>
              <span className="px-3 py-1 rounded-full text-white text-sm font-medium" style={{ backgroundColor: spec.color }}>
                {face ? t(spec.label) : `${t(spec.label)}　${side === "back" ? t("裏面") : t("表面")}`}
              </span>
              <button type="button" onClick={() => setAutoMode((m) => !m)} className={`px-3 py-1 rounded-full text-xs font-medium border ${autoMode ? "bg-white text-black border-white" : "bg-black/40 text-white border-white/40"}`} data-testid="camera-auto-toggle">
                {t("自動")}: {autoMode ? "ON" : "OFF"}
              </button>
            </div>
            <div className="absolute bottom-24 left-0 right-0 text-center" style={{ zIndex: 3 }}>
              <p className="text-white text-sm bg-black/50 inline-block px-4 py-1.5 rounded-full">
                {autoMode && autoProgress > 0.05 ? t("そのまま静止してください") : (face ? t("枠内に顔を合わせてください") : t("枠内にカードを合わせてください"))}
              </p>
            </div>
          </div>
        )}
        <canvas ref={canvasRef} className="hidden" />
      </div>
      <div className="bg-black py-6 px-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20 w-12 h-12 rounded-full p-0" data-testid="camera-close-btn"><X className="h-6 w-6" /></Button>
        <button onClick={handleCapture} disabled={!ready} className="relative w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-50 active:scale-95 transition-transform" data-testid="camera-capture-btn">
          {autoMode && (
            <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 64 64" style={{ pointerEvents: "none" }}>
              <circle cx="32" cy="32" r="30" fill="none" stroke={spec.color} strokeWidth="4" strokeDasharray={2 * Math.PI * 30} strokeDashoffset={(1 - autoProgress) * 2 * Math.PI * 30} strokeLinecap="round" />
            </svg>
          )}
          <div className="w-12 h-12 rounded-full bg-white" />
        </button>
        <Button variant="ghost" size="sm" onClick={toggleCamera} className="text-white hover:bg-white/20 w-12 h-12 rounded-full p-0" data-testid="camera-flip-btn"><RotateCcw className="h-5 w-5" /></Button>
      </div>
    </div>
  );
}
